import { Controller, useFormContext } from 'react-hook-form';
import { ToggleSwitch } from 'flowbite-react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useAuth } from '@restworld/feature-authentication';
import { HomeIcon } from '@heroicons/react/24/outline';
import { InputSlider } from '../../../../legacy/input-slider';
import { Heading } from '@restworld/ui-ds-base';

import {
  StepWrapper,
  SaveResearchFormFields,
  toggleAddressEditViewAtom,
  recordJobExplorerModalAtom,
} from '../job-explorer-save-research-v2';
import AddressEditView from './address-edit-view';
import { SaveResearchMap } from './save-research-map';
import SaveResearchAddressInput from './research-address-input';

export const AddressViewAndSelection = () => {
  const [addressEditView, toggleAddressEditView] = useAtom(toggleAddressEditViewAtom);

  return addressEditView ? <AddressEditView callback={() => toggleAddressEditView(false)} /> : <MainAddressView />;
};

export default AddressViewAndSelection;

const MainAddressView = () => {
  return (
    <StepWrapper>
      <div data-cy="main-address-view" className="flex flex-col gap-8">
        <AddressTitle />
        <SaveResearchMap />
        <AddressInputSection />
        <SearchRadiusSection />
        <SeasonalFilter />
      </div>
    </StepWrapper>
  );
};

const AddressTitle = () => {
  const { isAuthenticated } = useAuth();
  const recordJobExplorerModal = useAtomValue(recordJobExplorerModalAtom);
  const { t } = useTranslation();

  return (
    <div className="flex gap-2 flex-col">
      <Heading size={3} className="leading-[28.8px]" testId="address-select-title">
        {!isAuthenticated || recordJobExplorerModal
          ? t('job-explorer-page:dont_miss_a_single_job_position')
          : t('job-explorer-page:enter_the_location_of_interest_for_the_search')}
      </Heading>
      <Heading size={5} weight="normal" color="muted" className="leading-[24px]">
        {t('job-explorer-page:enter_a_specific_address_or_city')}
      </Heading>
    </div>
  );
};

const AddressInputSection = () => {
  const [, toggleAddressEditView] = useAtom(toggleAddressEditViewAtom);
  const { watch, getValues } = useFormContext<SaveResearchFormFields>();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <Heading size={5} weight="normal" className="leading-[24px]">
        {t('job-explorer-page:reference_location')}
      </Heading>
      {isAuthenticated ? (
        <>
          {getValues('address.address') ? (
            <span className="flex flex-row items-center gap-1.5">
              <HomeIcon className="h-6 w-6 text-gray-600" />
              <Heading>{getValues('address.address')}</Heading>
            </span>
          ) : null}
          <a
            href="#!"
            className="text-primary underline text-sm font-semibold"
            onClick={() => toggleAddressEditView(true)}
          >
            {t('common:edit')}
          </a>
        </>
      ) : (
        <>
          <SaveResearchAddressInput />
          {!watch('address') && (
            <Heading size={6} weight="normal" color="muted" className="leading-[21px]">
              {t('job-explorer-page:will_use_this_information_to_define_the_search_area')}
            </Heading>
          )}
        </>
      )}
    </div>
  );
};

const SearchRadiusSection = () => {
  const { watch, control } = useFormContext<SaveResearchFormFields>();
  const addressDistance = watch('area_distance');
  const { t } = useTranslation();
  const addressValue = watch('address');
  return (
    <div
      className={`flex flex-col gap-2 ${
        !addressValue ? 'cursor-not-allowed saturate-25 opacity-50 pointer-events-none' : ''
      }`}
    >
      <div className="flex gap-0 flex-row">
        <Heading size={5} weight="normal" className="leading-[24px]">
          {t('job-explorer-page:search_radius')}
        </Heading>
        <Heading size={5} weight="normal" color="error" className="leading-[24px]">
          *
        </Heading>
      </div>
      {addressValue ? (
        <div className="flex justify-between items-center">
          <Heading size={6} weight="normal" color="muted-foreground" className="leading-[21px] w-[15rem] truncate">
            {addressValue?.address ? `Da ${addressValue?.address}` : 'Da -'}
          </Heading>
          <Heading size={5} color="primary" className="leading-[24px]">
            {addressDistance?.toString() + 'km'}
          </Heading>
        </div>
      ) : null}
      <Controller
        name="area_distance"
        control={control}
        render={({ field }) => (
          <InputSlider
            data-cy="save-research-area-distance-slider"
            disabled={!addressValue}
            trackStyle={[
              {
                backgroundColor: '#693eb5',
                height: 8,
                borderRadius: 4,
              },
            ]}
            handleStyle={[
              {
                backgroundColor: '#ffffff',
                // 28px: a 20px thumb was too small to grab on a phone; marginTop re-centres it on the 8px rail.
                height: 28,
                width: 28,
                marginTop: -10,
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                borderColor: '#E5E7EB',
                borderWidth: 2,
                opacity: 1,
              },
            ]}
            railStyle={{
              backgroundColor: '#E5E7EB',
              height: 8,
              borderRadius: 4,
            }}
            {...{ min: 0, max: 100, step: 2 }}
            {...field}
          />
        )}
      />
    </div>
  );
};

export const SeasonalFilter = () => {
  const { control } = useFormContext<SaveResearchFormFields>();
  const { t } = useTranslation('job-explorer-page');
  return (
    <div className="flex justify-between items-center">
      <Heading>{t('work_with_accommodation')}</Heading>
      <Controller
        name="seasonal"
        control={control}
        render={({ field }) => (
          <ToggleSwitch
            data-cy="save-research-seasonal-filter"
            color="purple"
            label=""
            checked={Boolean(field.value)}
            {...field}
          />
        )}
      />
    </div>
  );
};
